/**
 * <TodoRow> — one line of the parent home's "À faire" block (molecule).
 *
 * Each row is one of the things the parent can settle in a tap: activation, reprise, changement
 * de tuteur, rattrapage, paiement. A short label, an optional detail, and ONE button — the whole row is
 * the button, so the CTA text is a label, not a second target.
 *
 * Pas de pastille rouge sur le nombre : « 2 absences, encore 3 jours pour la première » se lit
 * déjà dans le détail, et un compteur d'alerte sur une tâche que personne n'a ratée crie pour
 * rien. Le nombre vit dans la phrase, pas dans une gommette.
 *
 * **Elle ne dessine pas de carte** : les tâches forment UNE carte-liste à séparateurs. Le retrait
 * vient de `<CardListRow>`, pour que toutes les lignes s'alignent.
 *
 * The glyph per kind is an internal const: a call-site chooses the todo, never its icon.
 *
 * **Le ton arrive en donnée** (`tone`, posé par l'adaptateur), il ne se devine pas ici : une ligne
 * qui BLOQUE — un paiement en attente suspend les séances — ou qui PRESSE — un rattrapage à 7 jours
 * ou moins de son échéance — écrit son libellé en chip pleine et reprend le même ton sur son
 * action, pour être ce qu'on veut toucher en premier. Sans ton, la ligne reste une invitation
 * ordinaire. Posée seule sur une carte de son ton (`tinted`), elle n'a plus besoin de la chip : le
 * libellé reste du texte, à l'encre du ton.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge, type BadgeVariant } from "../../atoms/Badge"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { CardListRow } from "../CardList"
import { type UIParentTodo, type UIParentTodoTone } from "../../../ports/primary/ui/types"
import { type UIStudentTodoKind, type UITodoRowItem } from "../../../ports/primary/ui/types/studentHome"

/** La ligne « à faire » de tous les espaces (`ports/primary/ui/types/studentHome`), réexportée pour ses lecteurs du DS. */
export type { UIStudentTodoKind, UITodoRowItem }

/** Todo kind → glyph. Mirrors the legacy banner each item comes from. */
const TODO_ICON: Record<UITodoRowItem["kind"], IconName> = {
    activation: "bolt",
    reprise: "sync-alt",
    makeup: "redo",
    payment: "credit-card",
    tutor_change: "exchange-alt",
    trial_profile: "user",
    // Le masteur — les glyphes de l'ancien `TutorTaskBanner` (et de `TrainingCompletionCard`).
    prepare_live: "clipboard-check",
    missing_reports: "file-lines",
    update_availability: "calendar-check",
    complete_profile: "user",
    stripe_account: "credit-card",
    training: "graduation-cap",
}

/**
 * Ce qu'un ton peint : l'APLAT foncé de la chip (écrite en blanc) et l'ENCRE de l'action. Les
 * deux ne sont pas le même jeton — l'encre se pose sur la carte et s'éclaircit sur la nuit,
 * l'aplat EST un fond et ne s'inverse pas (`theme/tokens/colors`, § les aplats).
 */
const TONE_PAINT: Record<UIParentTodoTone, { fill: BadgeVariant; ink: SemanticColorKey }> = {
    warning: { fill: "warning-deep", ink: "warning-strong" },
    danger: { fill: "danger-deep", ink: "danger-strong" },
}

export type TodoRowProps<T extends UITodoRowItem = UIParentTodo> = {
    todo: T
    onPress?: (todo: T) => void
    /**
     * La ligne peut être écartée : une croix, à droite de l'action — le `BannerCard` de l'accueil
     * élève en porte une, qui le masque jusqu'au prochain passage. ABSENT (défaut) : pas de croix,
     * la ligne part quand la tâche est faite.
     */
    onDismiss?: () => void
    /**
     * La ligne est posée seule sur une carte teintée de son ton : la teinte dit déjà l'urgence, le
     * libellé reste du texte (pas de chip) écrit à l'encre du ton.
     */
    tinted?: boolean
    testID?: string
}

export function TodoRow<T extends UITodoRowItem = UIParentTodo>(props: TodoRowProps<T>) {
    const { todo, onPress, onDismiss, tinted = false, testID } = props
    const { kind, eyebrow, label, detail, cta, tone } = todo
    const paint = tone != null ? TONE_PAINT[tone] : null

    const row = (
        <Pressable
            testID={testID}
            onPress={() => onPress?.(todo)}
            disabled={onPress == null}
            accessibilityRole="button"
            style={onDismiss != null ? styles.body : undefined}
        >
            <CardListRow>
                <Row gap="3" align="center">
                    <Icon name={TODO_ICON[kind]} size={20} color={paint?.ink ?? "primary"} />

                    <Stack gap="1" style={styles.body}>
                        {eyebrow != null && eyebrow !== "" && (
                            <Text size="text-xs" weight="bold" color={paint?.ink ?? "warning-strong"} uppercase>
                                {eyebrow}
                            </Text>
                        )}
                        {paint != null && !tinted ? (
                            // Sans glyphe : la ligne en porte déjà un à gauche, au même ton.
                            <Badge text={label} variant={paint.fill} />
                        ) : (
                            <Text
                                weight="bold"
                                size="text-md"
                                color={tinted ? paint?.ink : undefined}
                                style={styles.body}
                            >
                                {label}
                            </Text>
                        )}
                        {detail != null && detail !== "" && (
                            <Text size="text-sm" color="text-muted">
                                {detail}
                            </Text>
                        )}
                    </Stack>

                    <Row gap="1" align="center" style={styles.cta}>
                        <Text weight="bold" size="text-md" color={paint?.ink ?? "primary"}>
                            {cta}
                        </Text>
                        <Icon name="chevron-right" size={16} color={paint?.ink ?? "text-muted"} />
                    </Row>
                </Row>
            </CardListRow>
        </Pressable>
    )

    if (onDismiss == null) return row

    // La croix est posée À CÔTÉ de la zone qui presse : un bouton dans un bouton ne s'annonce pas.
    return (
        <Row gap="0" align="center">
            {row}
            <View style={styles.dismiss}>
                <IconButton
                    iconName="times"
                    size={36}
                    appearance="transparent"
                    iconColor="text-muted"
                    onPress={onDismiss}
                    accessibilityLabel="Masquer"
                />
            </View>
        </Row>
    )
}

const styles = StyleSheet.create((theme) => ({
    body: {
        flex: 1,
        flexShrink: 1,
    },
    cta: {
        flexShrink: 0,
    },
    dismiss: {
        flexShrink: 0,
        paddingRight: theme.spacing["2"],
    },
}))
